import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Grid, Paper, Select } from "@material-ui/core";
import {
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import styled from "@emotion/styled";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { getCurrentDate, getFirstDayOfMonth } from "@thequantumhub/iam-package";

import { SnackAuditQuestions } from "./DiningFormQuestions";
import { DiningAuditQuestions } from "./DiningFormQuestions";
import { getFacilities } from "../../../service/endpoint-manager";
import ButtonTextLoading from "../Shared/ButtonTextLoading";
import { diningAuditValidationSchema } from "../../../utils/diningAuditValidationSchema";
import { createRecordDiningAudit } from "../../../service/endpoint-manager";

import styles from "../../../styles.module.css";

const useStyles = makeStyles((theme) => ({
  formPaper: {
    padding: theme.spacing(2),
  },
  toggleContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    margin: theme.spacing(1),
  },
}));

const MuiToggleButton = styled(ToggleButton)(() => ({
  minWidth: "80px !important",
  height: "34px",
  borderRadius: "0px !important",
  color: "#415970",
  "&:first-child": {
    borderTopLeftRadius: "6px !important",
    borderBottomLeftRadius: "6px !important",
  },
  "&:last-child": {
    borderTopRightRadius: "6px !important",
    borderBottomRightRadius: "6px !important",
  },
  "&:hover": {
    backgroundColor: "#FFD7EB",
  },
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: "#F516AA",
    color: "white",
  },
}));

const ToggleButtonGroupField = ({
  name,
  value,
  options,
  setFieldValue,
  outcomeExistsPath,
}) => {
  const classes = useStyles();
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(e, value) => {
        if (outcomeExistsPath) {
          setFieldValue(outcomeExistsPath, value === "yes" ? true : false);
        }
        if (value) {
          setFieldValue(name, value);
        } else {
          setFieldValue(name, "");
        }
      }}
      aria-label={name}
      className={clsx(classes.toggleButtonGroup, "togglePickerPPE")}
    >
      {options.map((option) => (
        <MuiToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.label}
        >
          {option.label}
        </MuiToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const Guidelines = ({ showGuidelines, setShowGuidelines }) => {
  return (
    <Paper className={styles.formPaper} style={{ padding: "30px" }}>
      <Grid container justifyContent="space-between">
        <Grid item xs={10}>
          <Typography variant="h2" className={styles.momentTitle}>
            Definitions and Guidelines for Audit Tool Completion:
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Button
            type="button"
            variant="outlined"
            className={styles.redButton}
            onClick={() => setShowGuidelines(false)}
            style={{ marginLeft: "auto" }}
          >
            Close Guidelines
          </Button>
        </Grid>
      </Grid>

      <Typography
        variant="body2"
        className={styles.observationPointMargin}
        style={{ padding: "0px 20px" }}
      >
        <ol>
          <li>
            Is the menu posted near the dining area? The menu for the day is
            posted in or in close proximity to the dining area. It must be
            visible to residents.
          </li>
          <li>
            Are mealtimes free from distractions? E.g., the TV is off, or if on,
            the volume and programming is appropriate? If music is used, is it
            suitable to the population?
          </li>
          <li>
            Is the dining room clean; has a pleasant odor, free from debris on
            floor, spills cleaned promptly, and furniture in good repair?
            Lighting adequate (Overall the dining room is aesthetically
            pleasing. lighting adequate for the residents to see their food)? Is
            the dining room too hot or too cold?
          </li>
          <li>
            Is there clear access to tables by residents and staff (Adequate
            space available for residents and staff to move around. Space is
            available for wheelchairs and walkers. Staff can easily maneuver
            between tables to serve and give assistance)?
          </li>
          <li>
            Meals served at the same time for everyone at the same table? Ensure
            all residents at one table are done being served before starting
            another one.
          </li>
          <li>
            Are residents seated with appropriate table mates (people with like
            views or like capabilities are seated together)?
          </li>
          <li>
            Is the table setting, i.e., cutlery, dishes, visually appealing and
            suited to needs of those in care (items are not distracting,
            cracked, chipped or discolored)?
          </li>
          <li>
            Were residents that needed assistance provided a meal prior to the
            PSW being able to assist? Was food placed at the table before there
            was a PSW to assist?
          </li>
          <li>
            Adaptive equipment / utensils used according to residents' needs
            (Small cups, 3 handled mugs, built-up plate)?
          </li>
          <li>Is staff responding to residents' difficulties with feeding?</li>
          <li>
            Do residents who independently feed themselves receive appropriate
            set-up?
          </li>
          <li>
            Residents who require assistance with feeding are encouraged to eat
            and drink as independently as possible, but given support as
            necessary.
          </li>
          <li>
            Were the resident's hands washed pre-meal and was the resident's
            face/hands washed post meal? Was a clothing protector used?
          </li>
          <li>
            Safe feeding practices are observed? Staff is feeding resident at
            eye level, residents are alert, safe intake is supported (resident
            is seated upright, feet supported; proper head position).
          </li>
          <li>
            Is staff conversing with residents and/or staff-to-staff
            conversation appropriate and relevant to resident care?
          </li>
          <li>
            Does the meal experience appear dignified? Those who require pureed
            food, are the menu items kept separately or mixed together? Is the
            residents' mouth wiped appropriately, clothing protector changed as
            needed? Is the resident treated like an adult?
          </li>
          <li>
            Does the caregiver talk to the resident about the food provided and
            residents' preferences for receiving it (menu items are not mixed
            together unless desire is otherwise expressed)?
          </li>
          <li>
            Are all choking hazards removed from the table? For example,
            “thickener” should not be on the table unless it is actually being
            used and monitored.
          </li>
          <li>
            Are cues used to encourage interest in each meal (verbal, tactile
            and visual clues)?
          </li>
          <li>
            Is each resident offered a choice from the planned menu? Are
            individual preferences accommodated at mealtimes (Is each resident
            offered or visually shown choices for each meal, or for tray
            service, is there a means to accommodate those residents who don't
            like what they receive)?
          </li>
          <li>
            Awareness of appropriate food temperature for serving is evident.
            The auditor may not be able to take the temperature of the food
            being served, however, they can identify if staff serving are aware
            if the food temperature is too hot/too cold for the resident to eat.
          </li>
          <li>
            Pace of meal is served is appropriate (is one item provided at a
            time when appropriate).
          </li>
          <li>Residents are not rushed through their meal.</li>
          <li>
            For residents who eat slowly, is extra time beyond the normal
            mealtime is given?
          </li>
        </ol>
        <h3>Notes:</h3>
        <p>
          What were the main issues? Could they have been prevented? Were there
          any positive components? Any suggestions for improvement.
        </p>
      </Typography>
    </Paper>
  );
};

const DiningForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const loggedInUser = JSON.parse(localStorage.getItem("user-profile"));

  const [formFinalStep, setFormFinalStep] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");

  const toggleFormStep = () => {
    setFormFinalStep(!formFinalStep);
  };

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await getFacilities();
        if (response.data.status) {
          setFacilities(response.data.body);
          setSelectedFacility(response.data.body[0].name);
        }
      } catch (error) {
        console.error("Error fetching facilities: ", error);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    const selectedFacilityData = facilities.find(
      (facility) => facility.name === selectedFacility
    );
    setUnits(selectedFacilityData ? selectedFacilityData.units : []);
  }, [selectedFacility, facilities]);

  const toggleOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "n/a", label: "N/A" },
  ];

  const pairQuestions = (questions) => {
    let pairedQuestions = [];
    for (let i = 0; i < questions.length; i += 2) {
      pairedQuestions.push(questions.slice(i, i + 2));
    }
    return pairedQuestions;
  };

  // retrieve a nested property value from an object based on a given path.
  // If the path doesn't exist, return a default value
  const safeGet = (obj, path, defaultValue) => {
    return path.reduce((acc, part) => acc && acc[part], obj) || defaultValue;
  };

  // return the next date from current date
  function getTomorrowDate() {
    let currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + 1);

    let year = currentDate.getFullYear();
    let month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    let day = String(currentDate.getDate()).padStart(2, "0");

    let formattedDate = `${year}-${month}-${day}`;

    return formattedDate;
  }

  const getInitialValues = () => ({
    facility: "",
    unit: "",
    auditedBy: loggedInUser ? loggedInUser.userName : "",
    startDate: "",
    endDate: "",
    auditCompletedFor: "",
    mealObserved: "",
    snackAudit: {
      q1: { part1: { answer: "" } },
      q2: { part1: { answer: "" } },
      q3: { part1: { answer: "" } },
      q4: { part1: { answer: "" } },
      q5: { part1: { answer: "" } },
      q6: {
        part1: { answer: "" },
        part2: { answer: "" },
        part3: { answer: "" },
        part4: { answer: "" },
      },
      q7: { part1: { answer: "" } },
      q8: { part1: { answer: "" } },
      q9: {
        part1: { answer: "" },
        part2: { answer: "" },
        part3: { answer: "" },
      },
      q10: { part1: { answer: "" } },
      q11: { part1: { answer: "" } },
      q12: { part1: { answer: "" } },
      q13: { part1: { answer: "" } },
      q14: { part1: { answer: "" } },
      q15: {
        part1: { answer: "" },
        part2: { answer: "" },
        part3: { answer: "" },
      },
      q16: { part1: { answer: "" } },
      q17: { part1: { answer: "" } },
      q18: { part1: { answer: "" } },
      comments: "",
      actionPlan: "",
      followUpDate: getTomorrowDate(),
      compliance: {
        percentage: 0,
      },
    },
    diningAudit: {
      environment: {
        q1: { answer: "", comment: "" },
        q2: { answer: "", comment: "" },
        q3: { answer: "", comment: "" },
        q4: { answer: "", comment: "" },
        q5: { answer: "", comment: "" },
        compliance: {
          percentage: 0,
        },
      },
      setup: {
        q1: { answer: "", comment: "" },
        q2: { answer: "", comment: "" },
        q3: { answer: "", comment: "" },
        compliance: {
          percentage: 0,
        },
      },
      assistance: {
        q1: { answer: "", comment: "" },
        q2: { answer: "", comment: "" },
        q3: { answer: "", comment: "" },
        q4: { answer: "", comment: "" },
        q5: { answer: "", comment: "" },
        q6: { answer: "", comment: "" },
        compliance: {
          percentage: 0,
        },
      },
      activities: {
        q1: { answer: "", comment: "" },
        q2: { answer: "", comment: "" },
        q3: { answer: "", comment: "" },
        q4: { answer: "", comment: "" },
        q5: { answer: "", comment: "" },
        q6: { answer: "", comment: "" },
        compliance: {
          percentage: 0,
        },
      },
      interruption: {
        q1: { answer: "", comment: "" },
        q2: { answer: "", comment: "" },
        q3: { answer: "", comment: "" },
        q4: { answer: "", comment: "" },
        compliance: {
          percentage: 0,
        },
      },
      notes: "",
    },
  });

  const handleFormSubmit = (
    values,
    { setSubmitting, resetForm, setFieldValue }
  ) => {
    // calculating complaince percentage values of snack audit before the form submit
    const calculateSnackCompliancePercentage = (snackAudit) => {
      let totalQuestions = 0;
      let yesAnswers = 0;

      // Loop over all the questions in the snackAudit
      for (const questionKey in snackAudit) {
        if (
          snackAudit.hasOwnProperty(questionKey) &&
          questionKey !== "compliance" &&
          questionKey !== "comments" &&
          questionKey !== "actionPlan" &&
          questionKey !== "followUpDate"
        ) {
          const questionParts = snackAudit[questionKey];
          for (const partKey in questionParts) {
            if (questionParts.hasOwnProperty(partKey)) {
              totalQuestions++;
              if (questionParts[partKey].answer === "yes") {
                yesAnswers++;
              }
            }
          }
        }
      }

      return totalQuestions > 0 ? (yesAnswers / totalQuestions).toFixed(3) : 0;
    };

    // calculating complaince percentage values of dining audit categories before the form submit
    const calculateCompliancePercentage = (auditCategory) => {
      let totalQuestions = 0;
      let yesAnswers = 0;

      for (const questionKey in auditCategory) {
        if (
          auditCategory.hasOwnProperty(questionKey) &&
          questionKey !== "compliance"
        ) {
          const question = auditCategory[questionKey];
          totalQuestions++;
          if (question.answer === "yes") {
            yesAnswers++;
          }
        }
      }

      const compliancePercentage =
        totalQuestions > 0 ? (yesAnswers / totalQuestions).toFixed(3) : 0;
      auditCategory.compliance.percentage = compliancePercentage;

      return compliancePercentage;
    };

    const snackAuditCompliance = calculateSnackCompliancePercentage(
      values.snackAudit
    );

    const environmentCompliance = calculateCompliancePercentage(
      values.diningAudit.environment
    );
    const setupCompliance = calculateCompliancePercentage(
      values.diningAudit.setup
    );
    const assistanceCompliance = calculateCompliancePercentage(
      values.diningAudit.assistance
    );
    const activitiesCompliance = calculateCompliancePercentage(
      values.diningAudit.activities
    );
    const interruptionCompliance = calculateCompliancePercentage(
      values.diningAudit.interruption
    );

    setFieldValue("snackAudit.compliance.percentage", snackAuditCompliance);
    setFieldValue(
      "diningAudit.environment.compliance.percentage",
      environmentCompliance
    );
    setFieldValue("diningAudit.setup.compliance.percentage", setupCompliance);
    setFieldValue(
      "diningAudit.assistance.compliance.percentage",
      assistanceCompliance
    );
    setFieldValue(
      "diningAudit.activities.compliance.percentage",
      activitiesCompliance
    );
    setFieldValue(
      "diningAudit.interruption.compliance.percentage",
      interruptionCompliance
    );

    if (values.snackAudit.comments === "") {
      setFieldValue("snackAudit.comments", " - ");
    }

    const requestData = {
      facility: values.facility,
      unit: values.unit,
      auditedBy: values.auditedBy,
      startDate: values.startDate,
      endDate: values.endDate,
      auditCompletedFor: values.auditCompletedFor,
      mealObserved: values.mealObserved,
      snackAudit: {
        ...values.snackAudit,
        compliance: {
          percentage: snackAuditCompliance,
        },
      },
      diningAudit: values.diningAudit,
    };

    console.log("Form values: \n", requestData);

    createRecordDiningAudit(requestData)
      .then((response) => {
        enqueueSnackbar(`${response?.data?.message}`, {
          variant: "success",
        });
        setSubmitting(false);
        resetForm();
        console.log(response);
        window.location.reload();
      })
      .catch((error) => {
        enqueueSnackbar(`${error}`, {
          variant: "error",
        });
        console.error("Error submitting form:", error);
        setSubmitting(false);
      });
  };

  const errorText1 = (error) => {
    try {
      return (
        <div
          style={{
            color: "red",
            fontSize: "0.7rem",
            textAlign: "left",
            visibility: error ? "visible" : "hidden",
            marginTop: "0px",
            position: "absolute",
            bottom: "10px",
            left: "25px",
          }}
        >
          {error}
        </div>
      );
    } catch (e) {
      return "";
    }
  };

  const errorText2 = (error) => {
    try {
      return (
        <div
          style={{
            color: "red",
            fontSize: "0.7rem",
            textAlign: "left",
            visibility: error ? "visible" : "hidden",
            marginTop: "0px",
            position: "absolute",
            bottom: "-15px",
            left: "3px",
          }}
        >
          {error}
        </div>
      );
    } catch (e) {
      return "";
    }
  };

  const errorText3 = (error) => {
    try {
      return (
        <div
          style={{
            color: "red",
            fontSize: "0.7rem",
            textAlign: "left",
            visibility: error ? "visible" : "hidden",
            marginTop: "0px",
            position: "absolute",
            bottom: "20px",
            left: "35px",
          }}
        >
          {error}
        </div>
      );
    } catch (e) {
      return "";
    }
  };

  const hasErrors = (errors) => Object.keys(errors).length > 0;

  return (
    <div style={{ padding: "10px" }}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={diningAuditValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue, errors, isSubmitting }) => {
          return (
            <Form>
              {showGuidelines ? (
                <Guidelines
                  showGuidelines={showGuidelines}
                  setShowGuidelines={setShowGuidelines}
                />
              ) : (
                <>
                  <Paper className={styles.formPaper}>
                    <div className={styles.patientCareFormSectionBody}>
                      <Grid container alignItems="flex-start" spacing={6}>
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          style={{ position: "relative" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label
                              htmlFor="facility"
                              className={styles.narcoticsFormLabel}
                            >
                              Facility
                            </label>
                            <Field
                              as={Select}
                              name="facility"
                              variant="outlined"
                              value={values.facility}
                              onChange={(e) => {
                                setFieldValue("facility", e.target.value);
                              }}
                              fullWidth
                            >
                              {facilities.map((facility) => (
                                <MenuItem
                                  key={facility.name}
                                  value={facility.name}
                                >
                                  {facility.name}
                                </MenuItem>
                              ))}
                            </Field>
                          </div>
                          {errorText1(errors?.facility)}
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          style={{ position: "relative" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label
                              htmlFor="unit"
                              className={styles.narcoticsFormLabel}
                            >
                              Unit
                            </label>
                            <Field
                              as={Select}
                              name="unit"
                              variant="outlined"
                              value={values.unit}
                              onChange={(e) => {
                                setSelectedUnit(e.target.value);
                                setFieldValue("unit", e.target.value);
                              }}
                              fullWidth
                            >
                              {units.map((unit) => (
                                <MenuItem key={unit.id} value={unit.name}>
                                  {unit.name}
                                </MenuItem>
                              ))}
                            </Field>
                          </div>
                          {errorText1(errors?.unit)}
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          style={{ position: "relative" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label
                              htmlFor="auditCompletedFor"
                              className={styles.narcoticsFormLabel}
                            >
                              Audit Completed For
                            </label>
                            <TextField
                              name="auditCompletedFor"
                              value={values.auditCompletedFor}
                              onChange={(e) => {
                                setFieldValue(
                                  "auditCompletedFor",
                                  e.target.value
                                );
                              }}
                              variant="outlined"
                              fullWidth
                            />
                          </div>
                          {errorText1(errors?.auditCompletedFor)}
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          style={{ position: "relative" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label
                              htmlFor="auditedBy"
                              className={styles.narcoticsFormLabel}
                            >
                              Audited By
                            </label>

                            <TextField
                              name="auditedBy"
                              value={values.auditedBy}
                              disabled
                              variant="outlined"
                              fullWidth
                            />
                          </div>
                          {errorText1(errors?.auditedBy)}
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        alignItems="flex-start"
                        spacing={6}
                        style={{ paddingBottom: "15px" }}
                      >
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          style={{ position: "relative" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label
                              htmlFor="mealObserved"
                              className={styles.narcoticsFormLabel}
                            >
                              Meal Observed
                            </label>
                            <TextField
                              name="mealObserved"
                              value={values.mealObserved}
                              onChange={(e) => {
                                setFieldValue("mealObserved", e.target.value);
                              }}
                              variant="outlined"
                              fullWidth
                            />
                          </div>
                          {errorText1(errors?.mealObserved)}
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          style={{ position: "relative" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label
                              htmlFor="endDate"
                              className={styles.narcoticsFormLabel}
                            >
                              Start Date <br />
                            </label>
                            <TextField
                              type="date"
                              name="startDate"
                              value={values.startDate}
                              onChange={(e) => {
                                setFieldValue("startDate", e.target.value);
                              }}
                            />
                          </div>
                          {errorText1(errors?.startDate)}
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          style={{ position: "relative" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label
                              htmlFor="endDate"
                              className={styles.narcoticsFormLabel}
                            >
                              End Date <br />
                            </label>
                            <TextField
                              type="date"
                              name="endDate"
                              value={values.endDate}
                              onChange={(e) => {
                                setFieldValue("endDate", e.target.value);
                              }}
                            />
                          </div>
                          {errorText1(errors?.endDate)}
                        </Grid>
                      </Grid>
                    </div>
                  </Paper>

                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginBottom: "15px" }}
                  >
                    <Grid item xs={12} sm={3}>
                      <Typography variant="h2" className={styles.momentTitle}>
                        {formFinalStep ? "Dining Audit" : "Snack Audit"}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={2}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "20px",
                      }}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        className={styles.redButton}
                        onClick={() => setShowGuidelines(true)}
                      >
                        Guidelines
                      </Button>

                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        onClick={() => toggleFormStep()}
                        className={styles.primaryButton}
                        disabled={errors?.snackAudit}
                      >
                        {formFinalStep ? "Previous" : "Next"}
                      </Button>
                    </Grid>
                  </Grid>

                  {!formFinalStep ? (
                    <div>
                      {pairQuestions(SnackAuditQuestions).map(
                        (questionPair, pairIndex) => (
                          <div key={pairIndex}>
                            <Paper
                              className={styles.formPaper}
                              style={{
                                marginBottom: "20px",
                                padding: "10px 0",
                              }}
                            >
                              {questionPair.map((question, index) => (
                                <React.Fragment key={index}>
                                  {question.mainQuestion !== "" && (
                                    <Typography
                                      variant="body2"
                                      className={styles.observationPointMargin}
                                      style={{ padding: "0px 20px" }}
                                    >
                                      {question.mainQuestion}
                                    </Typography>
                                  )}
                                  {question.questions.map((q, i) => (
                                    <div
                                      style={{ padding: "5px 20px" }}
                                      key={i}
                                    >
                                      <Grid
                                        container
                                        justifyContent="space-between"
                                        alignItems="center"
                                        style={
                                          question.mainQuestion !== ""
                                            ? { paddingLeft: "20px" }
                                            : { padding: "0" }
                                        }
                                      >
                                        <Grid item xs={12} sm={7}>
                                          <Typography
                                            variant="body2"
                                            className={
                                              styles.observationPointMargin
                                            }
                                          >
                                            {q}
                                          </Typography>
                                        </Grid>
                                        <Grid
                                          item
                                          xs={12}
                                          sm={4}
                                          md={3}
                                          style={{ position: "relative" }}
                                        >
                                          <ToggleButtonGroupField
                                            name={`snackAudit.q${
                                              pairIndex * 2 + (index + 1)
                                            }.part${i + 1}.answer`}
                                            value={
                                              values.snackAudit[
                                                `q${
                                                  pairIndex * 2 + (index + 1)
                                                }`
                                              ][`part${i + 1}`].answer
                                            }
                                            onChange={(e) => {
                                              setFieldValue(
                                                `snackAudit.q${
                                                  pairIndex * 2 + (index + 1)
                                                }.part${i + 1}.answer`,
                                                e.target.value
                                              );
                                            }}
                                            options={toggleOptions}
                                            setFieldValue={setFieldValue}
                                            className={
                                              styles.observationPointMargin
                                            }
                                          />
                                          {errorText2(
                                            errors?.snackAudit?.[
                                              `q${pairIndex * 2 + (index + 1)}`
                                            ]?.[`part${i + 1}`]?.answer
                                          )}
                                        </Grid>
                                      </Grid>
                                    </div>
                                  ))}
                                  {index < questionPair.length - 1 && (
                                    <Divider style={{ margin: "10px 0" }} />
                                  )}
                                </React.Fragment>
                              ))}
                            </Paper>
                          </div>
                        )
                      )}
                      <div style={{ marginTop: "20px" }}>
                        <Paper className={styles.formPaper}>
                          <div
                            className={
                              styles.patientCareFormAdditionalCommentField
                            }
                          >
                            <TextField
                              label="Comments"
                              variant="outlined"
                              value={values.snackAudit.comments}
                              onChange={(e) => {
                                setFieldValue(
                                  "snackAudit.comments",
                                  e.target.value
                                );
                              }}
                              fullWidth
                              multiline
                              maxRows={6}
                              InputProps={{
                                rows: 5,
                                maxRows: 6,
                                multiline: true,
                                inputComponent: "textarea",
                              }}
                              classes={{
                                root: styles.narcoticsCommentBoxFieldRoot,
                              }}
                              className={"narcoticsCommentBoxField"}
                            />
                          </div>
                        </Paper>
                      </div>
                      <div style={{ marginTop: "20px" }}>
                        <Paper className={styles.formPaper}>
                          <div
                            className={
                              styles.patientCareFormAdditionalCommentField
                            }
                            style={{ position: "relative" }}
                          >
                            <TextField
                              label="Action Plan"
                              variant="outlined"
                              value={values.snackAudit.actionPlan}
                              onChange={(e) => {
                                setFieldValue(
                                  "snackAudit.actionPlan",
                                  e.target.value
                                );
                              }}
                              fullWidth
                              multiline
                              maxRows={6}
                              InputProps={{
                                rows: 5,
                                maxRows: 6,
                                multiline: true,
                                inputComponent: "textarea",
                              }}
                              classes={{
                                root: styles.narcoticsCommentBoxFieldRoot,
                              }}
                              className={"narcoticsCommentBoxField"}
                              style={{ marginBottom: "5px" }}
                            />
                            {errorText1(errors?.snackAudit?.actionPlan)}
                          </div>
                        </Paper>
                      </div>
                      <div style={{ marginTop: "20px" }}>
                        <Paper
                          className={styles.formPaper}
                          style={{ padding: "20px", position: "relative" }}
                        >
                          <TextField
                            label="Follow Up Date"
                            type="date"
                            name="snackAudit.followUpDate"
                            value={values.snackAudit.followUpDate}
                            onChange={(e) => {
                              setFieldValue(
                                "snackAudit.followUpDate",
                                e.target.value
                              );
                            }}
                            style={{ marginBottom: "4px" }}
                          />
                          {errorText1(errors?.snackAudit?.followUpDate)}
                        </Paper>
                      </div>

                      <div
                        className={styles.narcoticsFormFinalizeBtns}
                        style={{ marginTop: "30px" }}
                      >
                        <Button
                          type="reset"
                          variant="outlined"
                          className={styles.redButton}
                          // onClick={() => {}}
                          style={{ marginLeft: "auto" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          onClick={() => toggleFormStep()}
                          className={styles.primaryButton}
                          disabled={errors?.snackAudit}
                        >
                          {formFinalStep ? "Previous" : "Next"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {DiningAuditQuestions.map(
                        (categoryData, categoryIndex) => (
                          <div
                            key={categoryIndex}
                            style={{
                              marginTop: categoryIndex === 0 ? "10px" : "40px",
                            }}
                          >
                            <Paper className={styles.formPaper}>
                              <div
                                className={styles.patientCareFormSectionTitle}
                              >
                                <Typography
                                  variant="h6"
                                  className={styles.momentTitle}
                                >
                                  {categoryIndex + 1}. {categoryData.name}
                                </Typography>
                              </div>
                              {categoryData.questions.map(
                                (question, questionIndex) => {
                                  const categoryKey = categoryData.tag;
                                  const questionKey = `q${questionIndex + 1}`;
                                  const answer = safeGet(
                                    values,
                                    [
                                      "diningAudit",
                                      categoryKey,
                                      questionKey,
                                      "answer",
                                    ],
                                    ""
                                  );
                                  const comment = safeGet(
                                    values,
                                    [
                                      "diningAudit",
                                      categoryKey,
                                      questionKey,
                                      "comment",
                                    ],
                                    ""
                                  );

                                  return (
                                    <React.Fragment key={questionIndex}>
                                      <Grid
                                        container
                                        spacing={4}
                                        alignItems="center"
                                      >
                                        <Grid
                                          item
                                          sm={6}
                                          style={{ position: "relative" }}
                                        >
                                          <div
                                            className={
                                              styles.observationPointSectionMain
                                            }
                                          >
                                            <div
                                              className={styles.toggleContainer}
                                            >
                                              <Typography
                                                variant="body2"
                                                className={
                                                  styles.observationPointMargin
                                                }
                                              >
                                                {question}
                                              </Typography>
                                              <ToggleButtonGroupField
                                                name={`diningAudit.${categoryKey}.${questionKey}.answer`}
                                                value={answer}
                                                options={toggleOptions}
                                                setFieldValue={setFieldValue}
                                                className={
                                                  styles.observationPointMargin
                                                }
                                              />
                                            </div>
                                            {errorText3(
                                              errors?.diningAudit?.[
                                                categoryKey
                                              ]?.[questionKey]?.answer
                                            )}
                                          </div>
                                        </Grid>
                                        <Grid
                                          item
                                          sm={6}
                                          style={{ alignContent: "center" }}
                                        >
                                          <div
                                            className={
                                              styles.patientCareFormCommentField
                                            }
                                          >
                                            <TextField
                                              name={`diningAudit.${categoryKey}.${questionKey}.comment`}
                                              value={comment}
                                              onChange={(e) =>
                                                setFieldValue(
                                                  `diningAudit.${categoryKey}.${questionKey}.comment`,
                                                  e.target.value
                                                )
                                              }
                                              label="Comments"
                                              variant="outlined"
                                              fullWidth
                                              multiline
                                              maxRows={3}
                                              InputProps={{
                                                rows: 2,
                                                maxRows: 3,
                                                multiline: true,
                                                inputComponent: "textarea",
                                              }}
                                              classes={{
                                                root: styles.narcoticsCommentBoxFieldRoot,
                                              }}
                                              className={
                                                "narcoticsCommentBoxField"
                                              }
                                            />
                                          </div>
                                        </Grid>
                                      </Grid>
                                      {questionIndex <
                                        categoryData.questions.length - 1 && (
                                        <Divider />
                                      )}
                                    </React.Fragment>
                                  );
                                }
                              )}
                            </Paper>
                          </div>
                        )
                      )}
                      <div style={{ marginTop: "40px" }}>
                        <Paper className={styles.formPaper}>
                          <div
                            className={
                              styles.patientCareFormAdditionalCommentField
                            }
                          >
                            <TextField
                              label="Notes"
                              variant="outlined"
                              value={values.diningAudit.notes}
                              onChange={(e) => {
                                setFieldValue(
                                  "diningAudit.notes",
                                  e.target.value
                                );
                              }}
                              fullWidth
                              multiline
                              maxRows={6}
                              InputProps={{
                                rows: 5,
                                maxRows: 6,
                                multiline: true,
                                inputComponent: "textarea",
                              }}
                              classes={{
                                root: styles.narcoticsCommentBoxFieldRoot,
                              }}
                              className={"narcoticsCommentBoxField"}
                            />
                          </div>
                        </Paper>
                      </div>
                      <div className={styles.narcoticsFormFinalizeBtns}>
                        <Button
                          type="reset"
                          variant="outlined"
                          className={styles.redButton}
                          // onClick={() => {}}
                          style={{ marginLeft: "auto" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          className={styles.primaryButton}
                          disabled={isSubmitting || hasErrors(errors && errors)}
                        >
                          <ButtonTextLoading
                            isSubmitting={isSubmitting}
                            body={"SUBMIT"}
                          />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default DiningForm;
